import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { UserContext } from '../../UserContext';
import { useHttpClient } from '../hooks/useHttpClient';
import { useNotificacoes } from '../hooks/useNotificacoes';
import { usePreventDoubleClick } from '../hooks/usePreventDoubleClick';

import styles from './DashboardRateio.module.css';
import styles_global from '../css/global.module.css';
import styles_content from '../css/main-content.module.css';

function DashboardRateio() {
    const { user } = useContext(UserContext);
    const navigate = useNavigate();
    const location = useLocation();
    const { get } = useHttpClient();
    const { exibirErro } = useNotificacoes();

    const hoje = new Date();
    const mesAtual = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;

    const [usinas, setUsinas] = useState([]);
    const [filtros, setFiltros] = useState({
        idUsina: '',
        mes: mesAtual
    });
    const [dados, setDados] = useState(null);
    const [loading, setLoading] = useState(false);
    const buscou = useRef(false);

    useEffect(() => {
        if (user === null) {
            navigate('/login');
        }
    }, [user, navigate]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const idUsinaUrl = params.get('idUsina');
        const mesUrl = params.get('mes');

        if (idUsinaUrl || mesUrl) {
            setFiltros((prev) => ({
                ...prev,
                idUsina: idUsinaUrl || prev.idUsina,
                mes: mesUrl || prev.mes
            }));
        }
    }, [location.search]);

    useEffect(() => {
        if (!user?.empresa?.id) return;

        let ativo = true;

        const carregarUsinas = async () => {
            try {
                const resp = await get(`/hello-sol/usinas/buscar?idEmpresa=${user.empresa.id}`);
                if (!ativo) return;
                setUsinas(Array.isArray(resp) ? resp : (resp.content ?? []));
            } catch (err) {
                if (ativo) {
                    exibirErro(err.message || 'Erro ao carregar usinas.');
                }
            }
        };

        carregarUsinas();

        return () => {
            ativo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.empresa?.id]);

    const buscarDashboard = async () => {
        if (!filtros.idUsina) {
            exibirErro('Selecione a usina.');
            return;
        }

        if (!filtros.mes) {
            exibirErro('Selecione o mês.');
            return;
        }

        setLoading(true);

        try {
            const resp = await get(
                `/hello-sol/rateios/dashboard?idUsina=${filtros.idUsina}&competencia=${filtros.mes}`
            );

            setDados(resp);
            buscou.current = true;
        } catch (err) {
            setDados(null);
            buscou.current = true;
            exibirErro(err.message || 'Erro ao carregar dashboard.');
        } finally {
            setLoading(false);
        }
    };

    const [safeFetch] = usePreventDoubleClick(buscarDashboard);

    useEffect(() => {
        if (!filtros.idUsina || !filtros.mes) return;

        let ativo = true;

        const carregarInicial = async () => {
            try {
                setLoading(true);
                const resp = await get(
                    `/hello-sol/rateios/dashboard?idUsina=${filtros.idUsina}&competencia=${filtros.mes}`
                );
                if (!ativo) return;

                setDados(resp);
                buscou.current = true;
            } catch (err) {
                if (!ativo) return;
                setDados(null);
                buscou.current = true;
                exibirErro(err.message || 'Erro ao carregar dashboard.');
            } finally {
                if (ativo) {
                    setLoading(false);
                }
            }
        };

        carregarInicial();

        return () => {
            ativo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtros.idUsina, filtros.mes]);

    const handleSubmit = (e) => {
        e.preventDefault();
        safeFetch();
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFiltros((prev) => ({ ...prev, [name]: value }));
    };

    const formatNumber = (valor, scale = 2) =>
        Number(valor || 0).toLocaleString('pt-BR', {
            minimumFractionDigits: scale,
            maximumFractionDigits: scale
        });

    const getStatusClass = (status) => {
        if (status === 'OK') return styles.statusOk;
        if (status === 'ALERTA') return styles.statusAlerta;
        if (status === 'CRITICO') return styles.statusCritico;
        return styles.statusDefault;
    };

    const insights = dados?.insights ?? [];

    return (
        <div className={styles_global.container}>
            <h2>Dashboard de Rateio</h2>

            <form className={styles.filterForm} onSubmit={handleSubmit}>
                <div className={`${styles_content.formField} ${styles.usinaField}`}>
                    <label>Usina</label>
                    <select
                        name="idUsina"
                        value={filtros.idUsina}
                        onChange={handleChange}
                    >
                        <option value="">Selecione a Usina</option>
                        {usinas.map((u) => (
                            <option key={u.id} value={u.id}>
                                {u.nome}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={`${styles_content.formField} ${styles.mesField}`}>
                    <label>Mês</label>
                    <input
                        type="month"
                        name="mes"
                        value={filtros.mes}
                        onChange={handleChange}
                    />
                </div>

                <div className={styles.botoesAbaixo}>
                    <button type="submit" disabled={loading}>
                        {loading ? 'Consultando...' : 'Consultar'}
                    </button>

                    <button
                        type="button"
                        onClick={() =>
                            navigate(`/home/rateio-analise?idUsina=${filtros.idUsina}&mes=${filtros.mes}`)
                        }
                        disabled={!filtros.idUsina}
                    >
                        Ver Análise
                    </button>
                </div>
            </form>

            {loading && <p className={styles_global.loading}>Carregando...</p>}

            {!loading && dados && (
                <>
                    <div className={styles.resumoExecutivoBox}>
                        <h3>Resumo Executivo</h3>
                        <p>{dados.resumoExecutivo}</p>
                    </div>

                    <div className={styles.dashboardGrid}>
                        <div className={styles.card}>
                            <h4>Energia Produzida</h4>
                            <p>{formatNumber(dados.energiaProduzida)} kWh</p>
                        </div>

                        <div className={styles.card}>
                            <h4>Energia Distribuída (Teórica)</h4>
                            <p>{formatNumber(dados.energiaDistribuida)} kWh</p>
                        </div>

                        <div className={styles.card}>
                            <h4>Consumo Real</h4>
                            <p>{formatNumber(dados.energiaConsumidaReal)} kWh</p>
                        </div>

                        <div className={styles.card}>
                            <h4>Sobra Real</h4>
                            <p>{formatNumber(dados.energiaSobraReal ?? dados.economiaPotencial)} kWh</p>
                        </div>

                        <div className={styles.card}>
                            <h4>Eficiência</h4>
                            <p>{formatNumber(dados.eficienciaPercentual)}%</p>
                        </div>

                        <div className={styles.card}>
                            <h4>Desvio Médio</h4>
                            <p>{formatNumber(dados.desvioMedioPercentual)}%</p>
                        </div>

                        <div className={styles.card}>
                            <h4>Total de Unidades</h4>
                            <p>{dados.totalUnidades || 0}</p>
                        </div>

                        <div className={styles.card}>
                            <h4>Unidades com Problema</h4>
                            <p>{dados.unidadesComProblema || 0}</p>
                        </div>

                        <div className={styles.card}>
                            <h4>Unidades OK</h4>
                            <p>{dados.unidadesOk || 0}</p>
                        </div>

                        <div className={styles.card}>
                            <h4>Receberam Mais</h4>
                            <p>{dados.unidadesReceberamMais || 0}</p>
                        </div>

                        <div className={styles.card}>
                            <h4>Receberam Menos</h4>
                            <p>{dados.unidadesReceberamMenos || 0}</p>
                        </div>

                        <div className={`${styles.card} ${styles.cardDestaque}`}>
                            <h4>Status Geral</h4>
                            <p>
                                <span className={`${styles.statusBadge} ${getStatusClass(dados.statusGeral)}`}>
                                    {dados.statusGeral || '—'}
                                </span>
                            </p>
                        </div>
                    </div>

                    {insights.length > 0 && (
                        <div className={styles.insightsBox}>
                            <h3>Insights Executivos</h3>
                            <ul>
                                {insights.map((i, idx) => (
                                    <li key={idx}>{i.mensagem}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </>
            )}

            {!loading && buscou.current && !dados && (
                <p className={styles_global.noData}>Nenhum dado encontrado.</p>
            )}
        </div>
    );
}

export default DashboardRateio;